import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { acquisitionsApi, type DuplicateCheckResult, type Fund } from "@/lib/api/acquisitions";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { useAuth } from "@/contexts/AuthContext";
import { parseFormattedNumber } from "@/lib/formatters";
import { emptyRequestForm, type RequestFormState } from "./request-form-state";

type PendingAction = "draft" | "submit" | "delete" | null;

export function useAcquisitionRequestForm(editId?: string) {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isEdit = Boolean(editId);
  const [form, setForm] = useState<RequestFormState>(emptyRequestForm);
  const [funds, setFunds] = useState<Fund[]>([]);
  const [duplicates, setDuplicates] = useState<DuplicateCheckResult | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [pending, setPending] = useState<PendingAction>(null);

  useEffect(() => {
    acquisitionsApi.funds
      .list({ isActive: true, limit: 100 })
      .then((res) => setFunds(res.funds))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!editId || !user?.id) return;
    let cancelled = false;
    setLoading(true);
    acquisitionsApi.requests
      .getById(editId)
      .then((req) => {
        if (cancelled) return;
        if (req.status !== "DRAFT" && req.status !== "NEEDS_INFO") {
          toast.error("Only a draft, or a request sent back for more information, can be edited.");
          navigate(`/acquisitions/requests/${editId}`);
          return;
        }
        if (req.requesterId !== user.id) {
          toast.error("You can only edit your own drafts.");
          navigate("/acquisitions/requests");
          return;
        }
        const authors = req.authors?.filter(Boolean) ?? (req.author ? [req.author] : []);
        setForm({
          title: req.title,
          subtitle: req.subtitle ?? "",
          description: req.description ?? "",
          authors,
          editors: req.editors ?? [],
          isbn: req.isbn ?? "",
          issn: req.issn ?? "",
          publisher: req.publisher ?? "",
          placeOfPublication: req.placeOfPublication ?? "",
          publicationYear: req.publicationYear != null ? String(req.publicationYear) : "",
          edition: req.edition ?? "",
          format: req.format || "BOOK",
          quantity: String(req.quantity),
          estimatedCost: req.estimatedCost != null ? String(req.estimatedCost) : "",
          fundId: req.fundId ?? "",
          justification: req.justification ?? "",
          urgency: req.urgency,
        });
      })
      .catch(() => {
        toast.error("Failed to load request.");
        navigate("/acquisitions/requests");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editId, user?.id, navigate, toast]);

  const patch = (next: Partial<RequestFormState>) => setForm((prev) => ({ ...prev, ...next }));

  const checkDuplicates = async () => {
    if (!form.isbn.trim()) return;
    try {
      const result = await acquisitionsApi.requests.checkDuplicates(form.isbn.trim());
      setDuplicates(
        result.existingBiblios.length > 0 || result.openRequests.length > 0 ? result : null
      );
    } catch {
      // non-critical
    }
  };

  const buildPayload = () => ({
    title: form.title.trim(),
    subtitle: form.subtitle.trim() || null,
    description: form.description.trim() || null,
    authors: form.authors,
    editors: form.editors,
    isbn: form.isbn.trim() || null,
    issn: form.issn.trim() || null,
    publisher: form.publisher.trim() || null,
    placeOfPublication: form.placeOfPublication.trim() || null,
    publicationYear: form.publicationYear ? Number(form.publicationYear) : null,
    edition: form.edition.trim() || null,
    format: form.format || null,
    quantity: parseInt(form.quantity, 10) || 1,
    estimatedCost: parseFormattedNumber(form.estimatedCost) ?? null,
    fundId: form.fundId || null,
    justification: form.justification.trim() || null,
    urgency: form.urgency,
  });

  const handleSubmit = async (submitAfterSave: boolean) => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (form.publicationYear) {
      const year = Number(form.publicationYear);
      if (!/^\d{4}$/.test(form.publicationYear) || year < 1000 || year > 9999) {
        toast.error("Publication year must be a four-digit year.");
        return;
      }
    }
    setPending(submitAfterSave ? "submit" : "draft");
    try {
      if (isEdit && editId) {
        await acquisitionsApi.requests.update(editId, buildPayload());
        if (submitAfterSave) {
          await acquisitionsApi.requests.submit(editId);
          toast.success("Request updated and submitted for review.");
        } else {
          toast.success("Draft saved.");
        }
      } else {
        const created = await acquisitionsApi.requests.create(buildPayload());
        if (submitAfterSave) {
          await acquisitionsApi.requests.submit(created.id);
          toast.success("Request created and submitted for review.");
        } else {
          toast.success("Request saved as draft.");
        }
      }
      navigate("/acquisitions/requests");
    } catch (err: unknown) {
      toast.error(
        getApiErrorMessage(err, isEdit ? "Failed to update request." : "Failed to create request.")
      );
    } finally {
      setPending(null);
    }
  };

  const handleDeleteDraft = async () => {
    if (!editId || !isEdit) return;
    if (!window.confirm("Delete this draft permanently? This cannot be undone.")) return;
    setPending("delete");
    try {
      await acquisitionsApi.requests.delete(editId);
      toast.success("Draft deleted.");
      navigate("/acquisitions/requests");
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Could not delete draft."));
    } finally {
      setPending(null);
    }
  };

  return {
    isEdit,
    form,
    funds,
    duplicates,
    loading,
    pending,
    patch,
    checkDuplicates,
    handleSubmit,
    handleDeleteDraft,
    navigate,
  };
}
